import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  fetchChannels,
  fetchTimeline,
  saveScheduleEntry,
  deleteScheduleEntry,
  applyTemplate,
  fetchTemplates,
  materializeSchedule,
  previewScheduleAt,
} from "./channelScheduleApi";
import MediaAssetPicker from "../ContentStudio/MediaAssetPicker";
import "./BroadcastStudio.css";

const ZOOM_MINUTES = [15, 30, 60, 360, 1440];
const RECURRENCE_OPTIONS = [
  { value: "once", label: "Once" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

function parseIso(s) {
  return s ? new Date(s).getTime() : null;
}

function snapMs(ms, snapMinutes) {
  const step = snapMinutes * 60 * 1000;
  return Math.round(ms / step) * step;
}

/**
 * Timeline Studio — multi-channel schedule builder (Sprint 9 Phase B).
 */
function TimelineStudioPage() {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [zoomIdx, setZoomIdx] = useState(2);
  const [entries, setEntries] = useState([]);
  const [channelMeta, setChannelMeta] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("");
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftRecurrence, setDraftRecurrence] = useState("once");
  const [draftEntryKind, setDraftEntryKind] = useState("program");
  const [previewAt, setPreviewAt] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [saveWarning, setSaveWarning] = useState("");
  const dragRef = useRef(null);

  const snapMinutes = ZOOM_MINUTES[zoomIdx];
  const dayStart = useMemo(() => new Date(day + "T00:00:00"), [day]);
  const dayEnd = useMemo(() => new Date(dayStart.getTime() + 86400000), [dayStart]);
  const pxPerMin = zoomIdx <= 1 ? 4 : zoomIdx === 2 ? 2 : zoomIdx === 3 ? 0.5 : 0.2;
  const widthPx = 24 * 60 * pxPerMin;

  const load = useCallback(async () => {
    if (!channelId) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchTimeline({
        channel_id: channelId,
        from: dayStart.toISOString(),
        to: dayEnd.toISOString(),
      });
      if (!data) {
        setError("Unable to load timeline.");
        setEntries([]);
      } else {
        setChannelMeta(data.channel);
        setEntries(data.entries || []);
      }
    } catch (e) {
      setError(e.message || "Timeline load failed.");
    } finally {
      setLoading(false);
    }
  }, [channelId, dayStart, dayEnd]);

  useEffect(() => {
    (async () => {
      const list = await fetchChannels({ visibility: "public" });
      setChannels(list);
      if (list.length && !channelId) setChannelId(String(list[0].id));
      const t = await fetchTemplates({});
      setTemplates(t);
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pushHistory = (nextEntries) => {
    setHistory((h) => h.concat([entries]).slice(-30));
    setFuture([]);
    setEntries(nextEntries);
  };

  const undo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setFuture((f) => [entries].concat(f));
    setHistory((h) => h.slice(0, -1));
    setEntries(prev);
  };

  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setHistory((h) => h.concat([entries]));
    setFuture((f) => f.slice(1));
    setEntries(next);
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
      if (e.key === "Delete" && selectedId) {
        onDelete(selectedId);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const filtered = entries.filter((e) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (e.title || "").toLowerCase().indexOf(q) >= 0 || (e.entry_kind || "").indexOf(q) >= 0;
  });

  const onBarMouseDown = (e, entry, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(entry.id);
    const start = parseIso(entry.starts_at);
    const end = parseIso(entry.ends_at) || start + 3600000;
    dragRef.current = {
      id: entry.id,
      mode: mode,
      originX: e.clientX,
      start: start,
      end: end,
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const deltaMin = (e.clientX - d.originX) / pxPerMin;
      const deltaMs = deltaMin * 60000;
      setEntries((prev) =>
        prev.map((row) => {
          if (row.id !== d.id) return row;
          let ns = d.start;
          let ne = d.end;
          if (d.mode === "move") {
            ns = snapMs(d.start + deltaMs, snapMinutes);
            ne = snapMs(d.end + deltaMs, snapMinutes);
          } else if (d.mode === "resize-end") {
            ne = snapMs(Math.max(d.start + snapMinutes * 60000, d.end + deltaMs), snapMinutes);
          } else if (d.mode === "resize-start") {
            ns = snapMs(Math.min(d.end - snapMinutes * 60000, d.start + deltaMs), snapMinutes);
          }
          return Object.assign({}, row, {
            starts_at: new Date(ns).toISOString(),
            ends_at: new Date(ne).toISOString(),
            _dirty: true,
          });
        })
      );
    };
    const onUp = async () => {
      const d = dragRef.current;
      if (!d) return;
      dragRef.current = null;
      const row = entries.find((x) => x.id === d.id);
      if (row && row._dirty) {
        await saveScheduleEntry({
          id: row.id,
          channel_id: channelId,
          starts_at: row.starts_at,
          ends_at: row.ends_at,
          title: row.title,
          entry_kind: row.entry_kind,
        });
        pushHistory(entries.map((x) => Object.assign({}, x, { _dirty: false })));
        load();
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  });

  const onDelete = async (id) => {
    await deleteScheduleEntry(id);
    pushHistory(entries.filter((e) => e.id !== id));
  };

  const onAddProgram = async () => {
    setPickerOpen(true);
  };

  const onPickAsset = async (attached) => {
    const start = dayStart.getTime() + 9 * 3600000;
    const media =
      (attached && attached.media_asset) ||
      (attached && attached.media_asset_json) ||
      {};
    const presentationKind = String(
      media.presentationKind ||
        (media.metadata && media.metadata.presentation_kind) ||
        ""
    ).toLowerCase();
    const isLive = presentationKind === "live";
    const scheduleMode = String(
      (media.metadata && media.metadata.live_schedule_mode) ||
        (media.schedule && media.schedule.continuous ? "continuous" : "") ||
        "continuous"
    ).toLowerCase();
    const continuous = isLive && scheduleMode !== "timed";
    const plannedSecs = Number(
      (media.metadata && media.metadata.live_planned_duration_seconds) ||
        (media.schedule && media.schedule.plannedDurationSeconds) ||
        0
    );
    let endsAt = null;
    let entryKind = draftEntryKind || "program";
    if (isLive) {
      entryKind = "live_event";
      if (!continuous && plannedSecs > 0) {
        endsAt = new Date(start + plannedSecs * 1000).toISOString();
      } else if (!continuous) {
        endsAt = new Date(start + 3600000).toISOString();
      }
      // continuous: ends_at null — remains active until ended/unpublished/replaced
    } else {
      endsAt = new Date(start + 3600000).toISOString();
    }
    const body = await saveScheduleEntry({
      channel_id: channelId,
      title: media.title || attached.title || "Program",
      entry_kind: entryKind,
      recurrence: draftRecurrence || "once",
      starts_at: new Date(start).toISOString(),
      ends_at: endsAt,
      source_type: media.sourceType || (isLive ? "live_ingest" : "hosted"),
      live_state: isLive ? (continuous ? "live" : "upcoming") : null,
      admin_video_id: attached.admin_video_id,
      media_asset_key: attached.media_asset_key,
      media_asset_json: attached.media_asset_json || media,
    });
    if (body && body.data && body.data.conflicts && body.data.conflicts.length) {
      setSaveWarning(
        "Scheduled with conflicts on entries: " + body.data.conflicts.join(", ")
      );
    } else {
      setSaveWarning("");
    }
    if (body.success) load();
  };

  const selectedEntry = entries.find((e) => e.id === selectedId) || null;

  const onSaveSelectedMeta = async () => {
    if (!selectedEntry) return;
    const body = await saveScheduleEntry({
      id: selectedEntry.id,
      channel_id: channelId,
      title: selectedEntry.title,
      entry_kind: selectedEntry.entry_kind,
      recurrence: selectedEntry.recurrence || "once",
      recurrence_until: selectedEntry.recurrence_until || null,
      starts_at: selectedEntry.starts_at,
      ends_at: selectedEntry.ends_at,
    });
    if (body && body.data && body.data.conflicts && body.data.conflicts.length) {
      setSaveWarning(
        "Conflicts remain with entries: " + body.data.conflicts.join(", ")
      );
    } else {
      setSaveWarning("");
    }
    load();
  };

  const onPreview = async () => {
    if (!channelId || !previewAt) return;
    const body = await previewScheduleAt({
      channel_id: channelId,
      at: previewAt,
    });
    setPreviewResult(body);
  };

  const conflicts = entries.filter((e) => e.has_conflict);

  const onApplyTemplate = async (templateId) => {
    await applyTemplate({ template_id: templateId, channel_id: channelId, date: day });
    load();
  };

  return (
    <div className="bcast-studio">
      <Helmet>
        <title>Timeline Studio · TPG Broadcast</title>
      </Helmet>
      <header className="bcast-studio__header">
        <div>
          <p className="bcast-studio__eyebrow">TPG Broadcast Operations</p>
          <h1>Timeline Studio</h1>
        </div>
        <nav className="bcast-studio__nav">
          <Link to="/guide">EPG</Link>
          <Link to="/ops">Live Ops</Link>
          <Link to="/channels">Channels</Link>
        </nav>
      </header>

      <div className="bcast-studio__toolbar">
        <label>
          Channel
          <select value={channelId} onChange={(e) => setChannelId(e.target.value)}>
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.channel_number != null ? `#${c.channel_number} ` : ""}
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Day
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
        </label>
        <label>
          Zoom
          <select value={zoomIdx} onChange={(e) => setZoomIdx(Number(e.target.value))}>
            {ZOOM_MINUTES.map((m, i) => (
              <option key={m} value={i}>
                {m < 60 ? m + " min" : m === 60 ? "1 hr" : m / 60 + " hr"}
              </option>
            ))}
          </select>
        </label>
        <label>
          Search
          <input
            type="search"
            placeholder="Filter programs"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </label>
        <button type="button" className="bcast-btn" onClick={onAddProgram}>
          Schedule Media Asset
        </button>
        <label>
          New entry kind
          <select value={draftEntryKind} onChange={(e) => setDraftEntryKind(e.target.value)}>
            <option value="program">Program</option>
            <option value="premiere">Premiere</option>
            <option value="live_event">Live event</option>
            <option value="break">Break</option>
          </select>
        </label>
        <label>
          New recurrence
          <select value={draftRecurrence} onChange={(e) => setDraftRecurrence(e.target.value)}>
            {RECURRENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <nav className="bcast-studio__nav" style={{ marginLeft: "auto" }}>
          <Link to="/studio/library">Library</Link>
        </nav>
        <button type="button" className="bcast-btn bcast-btn--ghost" onClick={undo} disabled={!history.length}>
          Undo
        </button>
        <button type="button" className="bcast-btn bcast-btn--ghost" onClick={redo} disabled={!future.length}>
          Redo
        </button>
        <button
          type="button"
          className="bcast-btn bcast-btn--ghost"
          onClick={() => materializeSchedule({ channel_id: channelId, days: 7 })}
        >
          Materialize
        </button>
        {templates.length > 0 && (
          <label>
            Template
            <select
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) onApplyTemplate(e.target.value);
                e.target.value = "";
              }}
            >
              <option value="">Apply template…</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {error && <p className="bcast-studio__error">{error}</p>}
      {saveWarning && <p className="bcast-studio__error">{saveWarning}</p>}
      {loading && <p className="bcast-studio__status">Loading timeline…</p>}

      {conflicts.length > 0 && (
        <div className="bcast-studio__panel" style={{ marginBottom: "0.75rem" }}>
          <strong>{conflicts.length} conflict(s)</strong>
          <ul>
            {conflicts.map((c) => (
              <li key={c.id}>
                <button type="button" className="bcast-btn bcast-btn--ghost" onClick={() => setSelectedId(c.id)}>
                  {c.title || c.id}
                </button>{" "}
                overlaps another entry — select and rebook (drag/resize) or delete.
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bcast-studio__rail-wrap">
        <div className="bcast-studio__hours" style={{ width: widthPx }}>
          {Array.from({ length: 24 }).map((_, h) => (
            <div key={h} className="bcast-studio__hour" style={{ width: 60 * pxPerMin }}>
              {String(h).padStart(2, "0")}:00
            </div>
          ))}
        </div>
        <div className="bcast-studio__rail" style={{ width: widthPx }}>
          {filtered.map((entry) => {
            const start = parseIso(entry.starts_at);
            const end = parseIso(entry.ends_at) || start + 3600000;
            const left = ((start - dayStart.getTime()) / 60000) * pxPerMin;
            const width = Math.max(8, ((end - start) / 60000) * pxPerMin);
            const cls = [
              "bcast-studio__bar",
              "bcast-studio__bar--" + (entry.entry_kind || "program"),
              entry.has_conflict ? "is-conflict" : "",
              selectedId === entry.id ? "is-selected" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <div
                key={entry.id}
                className={cls}
                style={{ left: left, width: width }}
                title={entry.title}
                onMouseDown={(e) => onBarMouseDown(e, entry, "move")}
              >
                <span
                  className="bcast-studio__handle bcast-studio__handle--start"
                  onMouseDown={(e) => onBarMouseDown(e, entry, "resize-start")}
                />
                <span className="bcast-studio__bar-label">{entry.title || entry.entry_kind}</span>
                <span
                  className="bcast-studio__handle bcast-studio__handle--end"
                  onMouseDown={(e) => onBarMouseDown(e, entry, "resize-end")}
                />
              </div>
            );
          })}
        </div>
      </div>

      <p className="bcast-studio__hint">
        {channelMeta ? channelMeta.name + " · " + (channelMeta.timezone || "UTC") : ""} · Drag to move ·
        handles resize · snap {snapMinutes}m · Del deletes · Ctrl+Z undo · Schedule any authorized Media
        Asset (creator / org / platform) · Set recurrence before scheduling · Materialize expands series
      </p>

      {selectedEntry && (
        <div className="bcast-studio__panel" style={{ marginTop: "0.75rem" }}>
          <h3>Selected entry</h3>
          <div className="bcast-studio__toolbar">
            <label>
              Title
              <input
                value={selectedEntry.title || ""}
                onChange={(e) =>
                  setEntries((prev) =>
                    prev.map((row) =>
                      row.id === selectedEntry.id
                        ? Object.assign({}, row, { title: e.target.value })
                        : row
                    )
                  )
                }
              />
            </label>
            <label>
              Kind
              <select
                value={selectedEntry.entry_kind || "program"}
                onChange={(e) =>
                  setEntries((prev) =>
                    prev.map((row) =>
                      row.id === selectedEntry.id
                        ? Object.assign({}, row, { entry_kind: e.target.value })
                        : row
                    )
                  )
                }
              >
                <option value="program">Program</option>
                <option value="premiere">Premiere</option>
                <option value="live_event">Live event</option>
                <option value="break">Break</option>
              </select>
            </label>
            <label>
              Recurrence
              <select
                value={selectedEntry.recurrence || "once"}
                onChange={(e) =>
                  setEntries((prev) =>
                    prev.map((row) =>
                      row.id === selectedEntry.id
                        ? Object.assign({}, row, { recurrence: e.target.value })
                        : row
                    )
                  )
                }
              >
                {RECURRENCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Until
              <input
                type="date"
                value={
                  selectedEntry.recurrence_until
                    ? String(selectedEntry.recurrence_until).slice(0, 10)
                    : ""
                }
                onChange={(e) =>
                  setEntries((prev) =>
                    prev.map((row) =>
                      row.id === selectedEntry.id
                        ? Object.assign({}, row, {
                            recurrence_until: e.target.value || null,
                          })
                        : row
                    )
                  )
                }
              />
            </label>
            <button type="button" className="bcast-btn" onClick={onSaveSelectedMeta}>
              Save entry
            </button>
          </div>
        </div>
      )}

      <div className="bcast-studio__panel" style={{ marginTop: "0.75rem" }}>
        <h3>Schedule preview</h3>
        <div className="bcast-studio__toolbar">
          <label>
            At
            <input
              type="datetime-local"
              value={previewAt}
              onChange={(e) => setPreviewAt(e.target.value)}
            />
          </label>
          <button type="button" className="bcast-btn" onClick={onPreview}>
            Preview what airs
          </button>
        </div>
        {previewResult && (
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, maxHeight: 180, overflow: "auto" }}>
            {JSON.stringify(previewResult.data || previewResult, null, 2)}
          </pre>
        )}
      </div>

      <MediaAssetPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={onPickAsset}
        actor={{}}
      />
    </div>
  );
}

export default TimelineStudioPage;
